import React, { useEffect, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import type { NodeType } from '../../../types';
import { Button, Input } from '../../ui';

type CreateMode = 'create_company' | 'create_contact' | 'create_deal' | 'create_lead';
type CreateNodeType = 'CREATE_COMPANY' | 'CREATE_CONTACT' | 'CREATE_DEAL' | 'CREATE_LEAD';
type EntityFieldsKey = 'company' | 'contact' | 'deal' | 'lead';

interface CreateCompanyConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  nodeType?: NodeType;
}

type SelectOption = {
  value: string;
  label: string;
};

const modeByNodeType: Record<CreateNodeType, CreateMode> = {
  CREATE_COMPANY: 'create_company',
  CREATE_CONTACT: 'create_contact',
  CREATE_DEAL: 'create_deal',
  CREATE_LEAD: 'create_lead',
};

const settingsByMode: Record<
  CreateMode,
  {
    title: string;
    helperText: string;
    fieldsKey: EntityFieldsKey;
    fieldsTitle: string;
    recommendedField: string;
    fallbackLabel: string;
    defaultIdVariable: string;
  }
> = {
  create_company: {
    title: 'Criar Empresa',
    helperText: 'Cria uma empresa no Bitrix24 usando os campos configurados abaixo.',
    fieldsKey: 'company',
    fieldsTitle: 'Campos da empresa',
    recommendedField: 'TITLE',
    fallbackLabel: 'Título fallback (se TITLE não for definido)',
    defaultIdVariable: 'created_company_id',
  },
  create_contact: {
    title: 'Criar Contato',
    helperText: 'Cria um contato no Bitrix24 usando os campos configurados abaixo.',
    fieldsKey: 'contact',
    fieldsTitle: 'Campos do contato',
    recommendedField: 'NAME',
    fallbackLabel: 'Nome fallback (se NAME/LAST_NAME não forem definidos)',
    defaultIdVariable: 'created_contact_id',
  },
  create_deal: {
    title: 'Criar Negócio',
    helperText: 'Cria um negócio no Bitrix24 usando os campos configurados abaixo.',
    fieldsKey: 'deal',
    fieldsTitle: 'Campos do negócio',
    recommendedField: 'TITLE',
    fallbackLabel: 'Título fallback (se TITLE não for definido)',
    defaultIdVariable: 'created_deal_id',
  },
  create_lead: {
    title: 'Criar Lead',
    helperText: 'Cria um lead no Bitrix24 usando os campos configurados abaixo.',
    fieldsKey: 'lead',
    fieldsTitle: 'Campos do lead',
    recommendedField: 'TITLE',
    fallbackLabel: 'Título fallback (se TITLE não for definido)',
    defaultIdVariable: 'created_lead_id',
  },
};

function resolveMode(nodeType: NodeType | undefined, configMode: unknown): CreateMode {
  if (nodeType && nodeType in modeByNodeType) {
    return modeByNodeType[nodeType as CreateNodeType];
  }

  const normalized = String(configMode || '').trim().toLowerCase();
  if (normalized === 'create_contact') {
    return 'create_contact';
  }
  if (normalized === 'create_deal') {
    return 'create_deal';
  }
  if (normalized === 'create_lead') {
    return 'create_lead';
  }
  return 'create_company';
}

function normalizeFieldId(fieldId: unknown): string {
  return String(fieldId || '').trim().toUpperCase();
}

function normalizeOptions(rawItems: any): SelectOption[] {
  if (!rawItems) {
    return [];
  }

  if (Array.isArray(rawItems)) {
    return rawItems
      .map((item: any) => {
        const value = String(item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? '').trim();
        const label = String(item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value).trim();
        return { value, label };
      })
      .filter((item) => item.value);
  }

  if (typeof rawItems === 'object') {
    return Object.entries(rawItems)
      .map(([key, item]: [string, any]) => {
        if (item && typeof item === 'object') {
          const value = String(item?.ID ?? item?.VALUE ?? item?.value ?? item?.id ?? key).trim();
          const label = String(item?.VALUE ?? item?.NAME ?? item?.label ?? item?.TITLE ?? value).trim();
          return { value, label };
        }

        return {
          value: String(key).trim(),
          label: String(item ?? key).trim(),
        };
      })
      .filter((item) => item.value);
  }

  return [];
}

function parseMultiValue(raw: unknown): string[] {
  const value = String(raw ?? '').trim();
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Legacy fallback: comma-separated values.
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function resolveCrmStatusEntityId(
  targetEntity: EntityFieldsKey,
  fieldId: string,
  field: Record<string, any>,
): string | null {
  const explicitCandidates = [
    field?.statusType,
    field?.statusTypeId,
    field?.statusTypeName,
    field?.crmStatusType,
    field?.entityTypeId,
  ];

  for (const candidate of explicitCandidates) {
    const normalized = String(candidate || '').trim().toUpperCase();
    if (normalized) {
      return normalized;
    }
  }

  const normalizedFieldId = normalizeFieldId(fieldId);

  if (normalizedFieldId === 'TYPE_ID') {
    return targetEntity === 'deal' ? 'DEAL_TYPE' : 'SOURCE';
  }

  if (normalizedFieldId === 'SOURCE_ID') {
    return 'SOURCE';
  }

  if (normalizedFieldId === 'STAGE_ID' && targetEntity === 'deal') {
    return 'DEAL_STAGE';
  }

  return null;
}

function extractPipelineIdFromEntity(entityId: string): string {
  const normalized = String(entityId || '').trim().toUpperCase();
  const match = normalized.match(/^DEAL_STAGE(?:_(\d+))?$/);
  if (!match) {
    return '';
  }

  return match[1] ? String(match[1]) : '0';
}

function resolvePipelineId(
  rawValue: string,
  dealCategories: Array<{ ID?: string | number; NAME?: string }>,
): string {
  const normalizedRaw = String(rawValue || '').trim();
  if (!normalizedRaw) {
    return '';
  }

  for (const category of dealCategories) {
    const id = String(category?.ID ?? '').trim();
    if (id && id === normalizedRaw) {
      return id;
    }
  }

  const normalizedName = normalizedRaw.toLowerCase();
  for (const category of dealCategories) {
    const id = String(category?.ID ?? '').trim();
    const name = String(category?.NAME ?? '').trim().toLowerCase();
    if (id && name && name === normalizedName) {
      return id;
    }
  }

  return '';
}

function extractPipelineIdFromStageId(stageId: string): string {
  const normalized = String(stageId || '').trim().toUpperCase();
  if (!normalized) {
    return '';
  }

  const match = normalized.match(/^C(\d+):/);
  if (match) {
    return String(match[1]);
  }

  // Stages without C{pipelineId}: prefix belong to default pipeline.
  return '0';
}

export const CreateCompanyConfig: React.FC<CreateCompanyConfigProps> = ({
  config,
  onChange,
  nodeType,
}) => {
  const { bitrixFields } = useFlowBuilderStore();
  const fieldMappings = Array.isArray(config.fieldMappings) ? config.fieldMappings : [];
  const mode = resolveMode(nodeType, config.mode);
  const settings = settingsByMode[mode];
  const mappingMode = config.mappingMode === 'json_auto' ? 'json_auto' : 'manual';
  const isDealJsonAutoMode = mode === 'create_deal' && mappingMode === 'json_auto';

  useEffect(() => {
    if (config.mode !== mode) {
      onChange('mode', mode);
    }
  }, [config.mode, mode, onChange]);

  const dealCategories = useMemo(() => {
    return Array.isArray(bitrixFields?.dealCategories) ? bitrixFields.dealCategories : [];
  }, [bitrixFields?.dealCategories]);

  const entityFields = useMemo(() => {
    const fields = (bitrixFields?.[settings.fieldsKey] || {}) as Record<string, any>;
    const crmStatusByEntity = (bitrixFields?.crmStatusByEntity || {}) as Record<string, any>;

    return Object.entries(fields)
      .map(([fieldId, field]) => {
        const fieldObject = (field && typeof field === 'object') ? field : {};
        const normalizedFieldId = normalizeFieldId(fieldId);

        let options = normalizeOptions((fieldObject as any)?.items);

        if (options.length === 0 && mode === 'create_deal' && normalizedFieldId === 'CATEGORY_ID') {
          options = dealCategories
            .map((category: any) => {
              const id = String(category?.ID ?? '').trim();
              if (!id) {
                return null;
              }

              return {
                value: id,
                label: String(category?.NAME || `Pipeline ${id}`).trim() || `Pipeline ${id}`,
              };
            })
            .filter(Boolean) as SelectOption[];
        }

        if (options.length === 0 && String((fieldObject as any)?.type || '').trim().toLowerCase() === 'crm_status') {
          const statusEntityId = resolveCrmStatusEntityId(
            settings.fieldsKey,
            fieldId,
            fieldObject as Record<string, any>,
          );
          options = normalizeOptions(statusEntityId ? crmStatusByEntity[statusEntityId] : undefined);
        }

        return {
          id: fieldId,
          normalizedId: normalizedFieldId,
          label: (fieldObject as any)?.formLabel || (fieldObject as any)?.title || fieldId,
          options,
          isMultiple:
            (fieldObject as any)?.isMultiple === true ||
            String((fieldObject as any)?.isMultiple || (fieldObject as any)?.multiple || '')
              .trim()
              .toUpperCase() === 'Y',
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields, dealCategories, mode, settings.fieldsKey]);

  const fieldMap = useMemo(() => {
    return new Map(entityFields.map((field) => [field.normalizedId, field]));
  }, [entityFields]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of dealCategories) {
      const id = String(category?.ID ?? '').trim();
      if (!id) {
        continue;
      }

      const name = String(category?.NAME || `Pipeline ${id}`).trim();
      map.set(id, name || `Pipeline ${id}`);
    }

    if (!map.has('0')) {
      map.set('0', 'Pipeline Padrão');
    }

    return map;
  }, [dealCategories]);

  const getMappingIndexesByField = (fieldId: string): number[] => {
    const normalized = normalizeFieldId(fieldId);
    const indexes: number[] = [];

    fieldMappings.forEach((mapping: any, index: number) => {
      if (normalizeFieldId(mapping?.fieldId) === normalized) {
        indexes.push(index);
      }
    });

    return indexes;
  };

  const getFieldMappingValue = (fieldId: string): string => {
    const indexes = getMappingIndexesByField(fieldId);
    if (indexes.length === 0) {
      return '';
    }

    return String(fieldMappings[indexes[0]]?.value ?? '').trim();
  };

  const setFieldMappingValue = (fieldId: string, value: string) => {
    const normalizedFieldId = normalizeFieldId(fieldId);
    const normalizedValue = String(value ?? '').trim();
    const next = [...fieldMappings];
    const indexes = getMappingIndexesByField(normalizedFieldId);

    if (!normalizedValue) {
      for (const index of [...indexes].sort((a, b) => b - a)) {
        next.splice(index, 1);
      }
      onChange('fieldMappings', next);
      return;
    }

    if (indexes.length > 0) {
      const firstIndex = indexes[0];
      next[firstIndex] = {
        ...next[firstIndex],
        fieldId: normalizedFieldId,
        value: normalizedValue,
      };

      for (const index of indexes.slice(1).sort((a, b) => b - a)) {
        next.splice(index, 1);
      }

      onChange('fieldMappings', next);
      return;
    }

    next.push({
      id: crypto.randomUUID(),
      fieldId: normalizedFieldId,
      value: normalizedValue,
    });
    onChange('fieldMappings', next);
  };

  const updateMapping = (index: number, key: 'fieldId' | 'value', value: string) => {
    const next = [...fieldMappings];
    next[index] = { ...next[index], [key]: value };
    onChange('fieldMappings', next);
  };

  const updateFieldId = (index: number, fieldId: string) => {
    const next = [...fieldMappings];
    next[index] = {
      ...next[index],
      fieldId,
      value: '',
    };
    onChange('fieldMappings', next);
  };

  const addMapping = () => {
    onChange('fieldMappings', [
      ...fieldMappings,
      {
        id: crypto.randomUUID(),
        fieldId: '',
        value: '',
      },
    ]);
  };

  const removeMapping = (index: number) => {
    const next = [...fieldMappings];
    next.splice(index, 1);
    onChange('fieldMappings', next);
  };

  const specialDealFields = mode === 'create_deal'
    ? new Set(['CATEGORY_ID', 'STAGE_ID'])
    : new Set<string>();

  const visibleFieldMappings: Array<{ mapping: any; index: number }> = fieldMappings
    .map((mapping: any, index: number) => ({ mapping, index }))
    .filter(({ mapping }: { mapping: any; index: number }) => !specialDealFields.has(normalizeFieldId(mapping?.fieldId)));

  const selectableEntityFields = entityFields.filter((field) => !specialDealFields.has(field.normalizedId));

  const selectedPipelineRaw = getFieldMappingValue('CATEGORY_ID');
  const selectedPipelineId = resolvePipelineId(selectedPipelineRaw, dealCategories);

  const stageOptions = useMemo(() => {
    if (mode !== 'create_deal') {
      return [] as SelectOption[];
    }

    const crmStatusByEntity = (bitrixFields?.crmStatusByEntity || {}) as Record<string, any>;
    const options: SelectOption[] = [];
    const seen = new Set<string>();

    for (const [entityId, rawItems] of Object.entries(crmStatusByEntity)) {
      const normalizedEntityId = String(entityId || '').trim().toUpperCase();
      if (!normalizedEntityId.startsWith('DEAL_STAGE')) {
        continue;
      }

      const pipelineId = extractPipelineIdFromEntity(normalizedEntityId);
      if (!pipelineId) {
        continue;
      }

      if (selectedPipelineId && pipelineId !== selectedPipelineId) {
        continue;
      }

      const pipelineName = categoryNameById.get(pipelineId) || `Pipeline ${pipelineId}`;
      const items = normalizeOptions(rawItems);

      for (const item of items) {
        const dedupeKey = `${pipelineId}:${item.value}`;
        if (seen.has(dedupeKey)) {
          continue;
        }
        seen.add(dedupeKey);

        options.push({
          value: item.value,
          label: selectedPipelineId ? item.label : `${pipelineName}: ${item.label}`,
        });
      }
    }

    // Fallback for portals where crm.status.list does not return DEAL_STAGE entries.
    if (options.length === 0) {
      const dealFields = (bitrixFields?.deal || {}) as Record<string, any>;
      const stageField = (dealFields?.STAGE_ID && typeof dealFields.STAGE_ID === 'object')
        ? dealFields.STAGE_ID
        : null;
      const directItems = normalizeOptions(stageField?.items);

      for (const item of directItems) {
        const pipelineId = extractPipelineIdFromStageId(item.value);
        if (!pipelineId) {
          continue;
        }

        if (selectedPipelineId && pipelineId !== selectedPipelineId) {
          continue;
        }

        const dedupeKey = `${pipelineId}:${item.value}`;
        if (seen.has(dedupeKey)) {
          continue;
        }
        seen.add(dedupeKey);

        const pipelineName = categoryNameById.get(pipelineId) || `Pipeline ${pipelineId}`;
        options.push({
          value: item.value,
          label: selectedPipelineId ? item.label : `${pipelineName}: ${item.label}`,
        });
      }
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields, categoryNameById, mode, selectedPipelineId]);

  const isBitrixFieldsLoading = bitrixFields === null;

  const selectedStageRaw = getFieldMappingValue('STAGE_ID');
  const selectedStageValue = stageOptions.some((option) => option.value === selectedStageRaw)
    ? selectedStageRaw
    : '';

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-200 leading-relaxed">
        <strong className="text-blue-800 dark:text-blue-100">{settings.title}:</strong> {settings.helperText}
        <br />
        Você pode usar variáveis como <strong className="text-blue-800 dark:text-blue-100">{'{{sender_name}}'}</strong>,{' '}
        <strong className="text-blue-800 dark:text-blue-100">{'{{sender_phone}}'}</strong> e outras do fluxo.
      </div>

      {mode === 'create_deal' && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
            Pipeline e etapa do negócio
          </h4>

          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
              Pipeline
            </label>
            <select
              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
              value={selectedPipelineId}
              onChange={(e) => {
                setFieldMappingValue('CATEGORY_ID', e.target.value);
                // Prevent stale stage from a different pipeline.
                setFieldMappingValue('STAGE_ID', '');
              }}
            >
              <option value="">Não definir (Bitrix decide)</option>
              {dealCategories.map((category: any) => {
                const id = String(category?.ID ?? '').trim();
                if (!id) {
                  return null;
                }

                return (
                  <option key={id} value={id}>
                    {category?.NAME || `Pipeline ${id}`}
                  </option>
                );
              })}
            </select>
          </div>

          <Input
            label="Pipeline dinâmico (opcional)"
            placeholder="Ex: {{pipeline_id}}"
            value={selectedPipelineRaw}
            onChange={(e) => setFieldMappingValue('CATEGORY_ID', e.target.value)}
          />

          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
              Etapa
            </label>
            <select
              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
              value={selectedStageValue}
              onChange={(e) => setFieldMappingValue('STAGE_ID', e.target.value)}
            >
              <option value="">Não definir etapa</option>
              {stageOptions.map((option) => (
                <option key={`${option.value}:${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Etapa dinâmica (opcional)"
            placeholder="Ex: {{stage_id}} ou C1:NEW"
            value={selectedStageRaw}
            onChange={(e) => setFieldMappingValue('STAGE_ID', e.target.value)}
          />

          {isBitrixFieldsLoading && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Carregando pipelines e etapas desta conexão...
            </p>
          )}

          {!isBitrixFieldsLoading && stageOptions.length === 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Não foi possível carregar etapas para esta conexão. Você pode informar a etapa dinamicamente.
            </p>
          )}
        </div>
      )}

      {mode === 'create_deal' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
            Modo de criação dos campos
          </label>
          <select
            className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
            value={mappingMode}
            onChange={(e) => {
              const nextMode = e.target.value === 'json_auto' ? 'json_auto' : 'manual';
              onChange('mappingMode', nextMode);
              if (nextMode === 'json_auto' && !String(config.jsonSourceVariable || '').trim()) {
                onChange('jsonSourceVariable', '{{parsed_json}}');
              }
            }}
          >
            <option value="manual">Manual (campo por campo)</option>
            <option value="json_auto">JSON automático (IA)</option>
          </select>
        </div>
      )}

      {isDealJsonAutoMode ? (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
            Criação automática por JSON
          </h4>

          <Input
            label="Variável com JSON de entrada"
            placeholder="Ex: {{crm_campos_json}}"
            value={config.jsonSourceVariable ?? '{{parsed_json}}'}
            onChange={(e) => onChange('jsonSourceVariable', e.target.value)}
            helperText="Obrigatório no modo JSON automático."
          />

          <label className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={config.jsonSkipEmpty !== false}
              onChange={(e) => onChange('jsonSkipEmpty', e.target.checked)}
            />
            Ignorar valores vazios do JSON
          </label>

          <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-relaxed">
            O nó tenta casar cada chave do JSON com o <strong>FIELD_ID</strong> ou com o <strong>nome do campo</strong> do Bitrix24.
            Exemplo: <code>{'{"UF_CRM_1748961973":"email@exemplo.com","UF_CRM_1770809241":"Assunto"}'}</code>.
          </p>

          <p className="text-[11px] text-slate-500 dark:text-slate-300 leading-relaxed">
            Dica: você ainda pode definir manualmente <strong>Pipeline</strong> e <strong>Etapa</strong> no bloco acima.
          </p>
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">{settings.fieldsTitle}</h4>
            <Button variant="outline" size="sm" onClick={addMapping}>
              <Plus className="w-3 h-3 mr-1" />
              Campo
            </Button>
          </div>

          {visibleFieldMappings.length === 0 ? (
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Adicione ao menos um campo. Recomendado: <strong>{settings.recommendedField}</strong>.
            </p>
          ) : (
            <div className="space-y-2">
              {visibleFieldMappings.map(({ mapping, index }: { mapping: any; index: number }) => {
                const normalizedFieldId = normalizeFieldId(mapping?.fieldId);
                const selectedField = fieldMap.get(normalizedFieldId);
                const options = selectedField?.options || [];
                const isSelectable = options.length > 0;

                return (
                  <div key={mapping.id || index} className="grid grid-cols-12 gap-2 items-center">
                    <select
                      className="col-span-5 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                      value={mapping.fieldId || ''}
                      onChange={(e) => updateFieldId(index, e.target.value)}
                    >
                      <option value="">Selecione um campo</option>
                      {selectableEntityFields.map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.label} ({field.id})
                        </option>
                      ))}
                    </select>

                    {(() => {
                      if (isSelectable && selectedField?.isMultiple) {
                        const selectedValues = parseMultiValue(mapping.value);
                        return (
                          <select
                            multiple
                            className="col-span-6 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md min-h-[72px]"
                            value={selectedValues}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions).map((option) => option.value);
                              updateMapping(index, 'value', JSON.stringify(selected));
                            }}
                          >
                            {options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      }

                      if (isSelectable) {
                        const currentValue = String(mapping.value || '').trim();
                        const selectedValue = options.some((option) => option.value === currentValue)
                          ? currentValue
                          : '';

                        return (
                          <div className="col-span-6 space-y-1">
                            <select
                              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                              value={selectedValue}
                              onChange={(e) => updateMapping(index, 'value', e.target.value)}
                            >
                              <option value="">Selecione um valor</option>
                              {options.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                              value={mapping.value || ''}
                              onChange={(e) => updateMapping(index, 'value', e.target.value)}
                              placeholder="Ou use variável dinâmica {{...}}"
                            />
                          </div>
                        );
                      }

                      return (
                        <input
                          type="text"
                          className="col-span-6 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                          value={mapping.value || ''}
                          onChange={(e) => updateMapping(index, 'value', e.target.value)}
                          placeholder="Valor (aceita variáveis {{...}})"
                        />
                      );
                    })()}

                    <button
                      className="col-span-1 inline-flex justify-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      onClick={() => removeMapping(index)}
                      title="Remover campo"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {entityFields.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Não foi possível carregar os campos de {settings.fieldsKey === 'company' ? 'empresa' : settings.fieldsKey === 'contact' ? 'contato' : 'negócio'} do Bitrix para esta conexão.
            </p>
          )}
        </div>
      )}

      <Input
        label={settings.fallbackLabel}
        placeholder="Ex: {{sender_name}}"
        value={config.fallbackTitle || ''}
        onChange={(e) => onChange('fallbackTitle', e.target.value)}
      />

      <p className="text-[11px] text-slate-500 dark:text-slate-300">
        ID criado disponível automaticamente em <code>{`{{${settings.defaultIdVariable}}}`}</code>.
      </p>
    </div>
  );
};
